/**
 * The phone (docs/ux/casefile.md, iPhone adaptation): a reading surface.
 * The run card, the review card, item cards and an item sheet with the pair,
 * the machine's sentences and the pages stacked. No controls, no Complete.
 */

import { Suspense, lazy } from 'react';
import type { CasefileManifest, SortReviewItem, SortRunHead } from '@/app/types';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { longDate } from '@/lib/format';
import { CATEGORY_LABEL, confidenceText, decisionWord, describeHuman, describeMachine, isEscalationWorthy, itemDoc, pagesText, viewerPlan, wallClockText } from './describe';
import { blockingSentence, completedSentence, ReviewStateChip, stateSentence, type ReviewCounts, type ReviewPhase } from './ReviewBar';
import { DecisionMark } from './ReviewQueue';
import { OpenPdfLink } from './ReplayPanel';
import { sortedPdfUrl } from './useSort';
import type { ViewerMode } from './ViewerDialog';

const PageRangeViewer = lazy(() => import('./PageRangeViewer'));

export function RunCard({ matterRef, head, onView }: { matterRef: string; head: SortRunHead; onView: (mode: ViewerMode) => void }) {
  const m = head.machine;
  const built = head.reviewedPointer !== null;
  return (
    <section aria-label="Sort run" data-slot="replay-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
      <span className="inline-flex h-6 w-fit items-center rounded-full bg-beam-warning-bg px-2.5 text-xs font-medium text-beam-warning">{m.modeLabel}</span>
      <p>Replay of run <span className="tabular break-all">{head.runId}</span>, {longDate(m.completedAt)}: <span className="tabular">{m.totalPages}</span> pages, {wallClockText(m.wallClockMs)}, <span className="tabular">{m.reviewItems}</span> review {m.reviewItems === 1 ? 'item' : 'items'}</p>
      <Button type="button" variant="outline" className="h-11" onClick={() => onView('unsorted')}>View Unsorted Case Files</Button>
      <Button type="button" variant="outline" className="h-11" onClick={() => onView('sorted')}>{built ? 'View Sorted Case Files (reviewed)' : 'View Sorted Case Files'}</Button>
      {/* The built deliverable stays a file link on the phone: an inline PDF shows one page on iOS. */}
      {built ? <OpenPdfLink href={sortedPdfUrl(matterRef, head.runId, 'reviewed')}>Open reviewed Sorted.pdf</OpenPdfLink> : null}
    </section>
  );
}

export function ReviewCard({ head, counts, phase, machine, reviewed }: { head: SortRunHead; counts: ReviewCounts; phase: ReviewPhase; machine: CasefileManifest; reviewed: CasefileManifest | null }) {
  return (
    <section aria-label="Review" data-slot="review-bar" className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
      <ReviewStateChip head={head} phase={phase} mine={false} />
      {phase === 'review' ? (
        <>
          <p>{stateSentence(head, counts, null)}</p>
          <p>{blockingSentence(counts)}</p>
          <p className="text-[13px] text-beam-text-muted">Items are decided and the review completed on a desktop. You can read each item and open the Sorted.pdf here.</p>
        </>
      ) : (
        <>
          <p>{completedSentence(head, counts)}</p>
          {phase === 'rebuilding' ? <p role="status">Rebuilding the reviewed casefile.</p> : null}
          {phase === 'rebuild_failed' ? <p role="alert" className="text-beam-error">The reviewed casefile could not be built. The decisions are kept and the machine result is unchanged.</p> : null}
          {phase === 'built' && reviewed ? (
            <dl className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 text-[13px]">
              <dt aria-hidden="true" /><dd className="eyebrow text-right text-beam-text-muted">Machine</dd><dd className="eyebrow text-right text-beam-text-muted">Reviewed</dd>
              <dt className="text-beam-text-muted">Documents</dt><dd className="tabular text-right">{machine.documents.length}</dd><dd className="tabular text-right">{reviewed.documents.length}</dd>
              <dt className="text-beam-text-muted">Billable pages</dt><dd className="tabular text-right">{machine.billablePages}</dd><dd className="tabular text-right">{reviewed.billablePages}</dd>
              <dt className="text-beam-text-muted">Duplicate pages</dt><dd className="tabular text-right">{machine.duplicatePages}</dd><dd className="tabular text-right">{reviewed.duplicatePages}</dd>
            </dl>
          ) : null}
        </>
      )}
    </section>
  );
}

export function ItemCards({ items, manifest, threshold, onOpen }: { items: SortReviewItem[]; manifest: CasefileManifest; threshold: number; onOpen: (itemId: string) => void }) {
  return (
    <section aria-label="Review items" className="flex flex-col gap-2">
      <h2 className="eyebrow px-1 text-beam-text-muted">Review items <span className="tabular">{items.length}</span></h2>
      <ul className="flex flex-col gap-2">
        {items.map((item) => {
          const doc = itemDoc(item, manifest);
          return (
            <li key={item.itemId}>
              <article data-item-id={item.itemId} className="rounded-xl border border-beam-line bg-beam-paper">
                <button type="button" onClick={() => onOpen(item.itemId)} className="flex min-h-11 w-full flex-col items-start gap-1 p-4 text-left text-sm">
                  <DecisionMark word={decisionWord(item)} />
                  <span className="flex flex-wrap gap-x-2"><span>{CATEGORY_LABEL[item.category]}</span><span className="tabular text-beam-text-muted">{pagesText(item.startPage, item.endPage)}</span></span>
                  {doc ? <span className="text-[13px] text-beam-text-muted">{doc.title}</span> : null}
                  {isEscalationWorthy(item, threshold) ? <span className="text-[13px] font-medium text-beam-warning">Escalation-worthy ({confidenceText(item.confidence)})</span> : null}
                </button>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function ItemSheet({ matterRef, runId, item, manifest, threshold, open, onOpenChange }: { matterRef: string; runId: string; item: SortReviewItem | null; manifest: CasefileManifest; threshold: number; open: boolean; onOpenChange: (open: boolean) => void }) {
  const plan = item ? viewerPlan(item, manifest) : null;
  const machine = item ? describeMachine(item, manifest) : null;
  const human = item ? describeHuman(item, manifest) : null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The sheet's own 28 px X is replaced by a 44 px Close in the header (docs/ux/casefile.md, iPhone adaptation). */}
      <SheetContent side="bottom" showCloseButton={false} className="max-h-[92svh] overflow-y-auto rounded-t-[20px] px-4 shadow-beam-3">
        {item && plan && machine && human ? (
          <div className="flex flex-col gap-4 pb-6">
            <SheetHeader className="px-0">
              <div className="flex items-start justify-between gap-3">
                <span className="eyebrow text-beam-text-muted">{CATEGORY_LABEL[item.category]}</span>
                <Button type="button" variant="ghost" size="lg" className="h-11 shrink-0 px-4" onClick={() => onOpenChange(false)}>Close</Button>
              </div>
              <SheetTitle>{pagesText(plan.extent.from, plan.extent.to)}</SheetTitle>
              <SheetDescription>Confidence {confidenceText(item.confidence)}.{isEscalationWorthy(item, threshold) ? ` Escalation-worthy (under ${threshold}).` : ''}</SheetDescription>
            </SheetHeader>
            <div data-slot="machine-human" className="flex flex-col gap-3">
              <div><span className="eyebrow text-beam-text-muted">Machine</span><p className="text-[16px] font-medium">{machine.sentence}</p>{machine.sub ? <p className="text-[13px] text-beam-text-muted">{machine.sub}</p> : null}</div>
              <div><span className="eyebrow text-beam-text-muted">Human</span><p className="text-[16px] font-medium">{human.sentence}</p>{item.by ? <p className="text-[13px] text-beam-text-muted">by {item.byName ?? item.by}</p> : null}</div>
            </div>
            {item.category !== 'additional' ? (
              <div className="flex flex-col gap-1 text-[13px]">
                <p><span className="font-medium">Why flagged: </span>{item.uncertainty}</p>
                <p><span className="font-medium">Look at: </span>{item.lookAt}</p>
              </div>
            ) : null}
            <span className="eyebrow text-beam-text-muted">{pagesText(plan.extent.from, plan.extent.to)}</span>
            <Suspense fallback={<Skeleton className="aspect-[595/842] w-full rounded-xl" />}>
              <PageRangeViewer matterRef={matterRef} runId={runId} plan={plan} yourBoundary={null} phone />
            </Suspense>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
